import React, { useEffect } from 'react';
import { useLanguage, LanguageItem } from '../context/LanguageContext';

interface FooterLanguageModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export default function FooterLanguageModal({ isOpen, onClose }: FooterLanguageModalProps) {
    const { language, setLanguage, languages, t } = useLanguage();

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && isOpen) {
                onClose();
            }
        };
        if (isOpen) {
            document.body.style.overflow = 'hidden';
            window.addEventListener('keydown', handleKeyDown);
        }
        return () => {
            document.body.style.overflow = '';
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const handleSelect = (code: string) => {
        setLanguage(code);
        onClose();
    };

    return (
        <div 
            className="fixed inset-0 z-[10001] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm transition-opacity duration-300"
            onClick={onClose}
        >
            <div 
                className="relative w-full max-w-md rounded-2xl border border-slider bg-bg p-6 shadow-2xl transition-all duration-300 flex flex-col overflow-hidden text-text"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-start justify-between pb-4 border-b border-slider">
                    <div>
                        <h3 className="text-xl font-bold text-text m-0 flex items-center gap-2">
                            <span>🌐</span>
                            <span>{t('footer.selectLanguageTitle', 'Select Website Language')}</span>
                        </h3>
                        <p className="text-xs text-text opacity-70 mt-1 mb-0 leading-relaxed">
                            {t('footer.languageDescription', 'Select your preferred language. Navigation, menus, and footer update instantly.')}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="rounded-full w-8 h-8 flex items-center justify-center text-text opacity-60 hover:opacity-100 hover:bg-hover transition-all border-none bg-transparent cursor-pointer"
                        aria-label="Close"
                    >
                        ✕
                    </button>
                </div>

                {/* Languages List */}
                <div className="py-4 space-y-2.5">
                    {languages.map((item: LanguageItem) => {
                        const isSelected = language === item.code;
                        return (
                            <button
                                key={item.code}
                                onClick={() => handleSelect(item.code)}
                                className={`w-full flex items-center justify-between p-3.5 rounded-xl border transition-all text-left cursor-pointer ${
                                    isSelected
                                        ? 'border-primary-cyan bg-primary-cyan/10 font-medium'
                                        : 'border-transparent bg-hover hover:border-slider hover:bg-hover/80'
                                }`}
                            >
                                <div className="flex items-center gap-3">
                                    <span className="text-2xl leading-none">{item.flag}</span>
                                    <div>
                                        <p className="m-0 text-base font-semibold text-text">
                                            {item.nativeName}
                                        </p>
                                        <p className="m-0 text-xs text-text opacity-60">
                                            {item.name} ({item.code.toUpperCase()})
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    {isSelected ? (
                                        <span className="w-6 h-6 rounded-full bg-primary-cyan text-white text-xs flex items-center justify-center font-bold">
                                            ✓
                                        </span>
                                    ) : (
                                        <span className="text-xs text-text opacity-40 hover:opacity-80">
                                            Select
                                        </span>
                                    )}
                                </div>
                            </button>
                        );
                    })}
                </div>

                {/* Footer notes */}
                <div className="pt-3 border-t border-slider flex items-center justify-between text-xs opacity-60">
                    <span>Active: <strong>{languages.find(l => l.code === language)?.name || 'English'}</strong></span>
                    <button
                        onClick={onClose}
                        className="px-3 py-1.5 rounded-lg border border-slider bg-hover text-text hover:border-primary-cyan cursor-pointer transition-colors"
                    >
                        {t('common.close', 'Close')}
                    </button>
                </div>
            </div>
        </div>
    );
}
